import { useEffect, useRef } from 'react'

import type { GrowerReportPeriod } from '@/lib/my-grower-report'
import { getAnalyticsActor, posthog } from '@/lib/posthog'

/** Người dùng THẤY gì sau khi số liệu về: có số, trống trơn, hay lỗi tải. */
export type MyGrowerReportOutcome = 'has_data' | 'empty' | 'load_error'

export interface MyGrowerReportView {
    /** Hộ đang xem — chỉ dùng làm khoá chống phát trùng, KHÔNG gửi đi. */
    growerId: string
    period: GrowerReportPeriod
    /** Bãi đang lọc — chỉ dùng làm khoá, KHÔNG gửi đi. Rỗng = tất cả bãi. */
    areaId: string
    /** Tài khoản gắn nhiều hộ (có ô chọn hộ). */
    multiGrower: boolean
    /** Cả hai nguồn số liệu (vật tư + tiền) đã về và không còn tải lại. */
    settled: boolean
    outcome: MyGrowerReportOutcome
}

/**
 * Mức dùng trang Báo cáo của hộ (M-45): phát `my_grower_report_viewed` MỘT lần
 * cho mỗi tổ hợp (hộ, kỳ, bãi) sau khi số liệu đã về. Đổi kỳ / bãi / hộ tính
 * lượt mới; tải lại nền cùng tổ hợp không phát thêm.
 *
 * Chỉ gửi enum/boolean + actor — KHÔNG gửi id/tên hộ, id/tên bãi, lượng vật tư
 * hay số tiền. Contract: `docs/analytics/my-grower-report.md`. Analytics lỗi
 * không được chặn việc xem báo cáo.
 */
export function useMyGrowerReportTracking(view: MyGrowerReportView) {
    const reported = useRef<string | null>(null)
    const { growerId, period, areaId, multiGrower, settled, outcome } = view

    useEffect(() => {
        if (!growerId || !settled) return
        const key = `${growerId}|${period}|${areaId}`
        if (reported.current === key) return
        const actor = getAnalyticsActor()
        if (!actor) return
        reported.current = key
        try {
            posthog.capture('my_grower_report_viewed', {
                schema_version: 1,
                ...actor,
                period,
                has_area_filter: !!areaId,
                multi_grower: multiGrower,
                outcome,
            })
        } catch {
            // Analytics không được chặn báo cáo.
        }
    }, [growerId, period, areaId, multiGrower, settled, outcome])
}
